/*
 * Purpose: Public pricing page — plans, features, Stripe checkout (theme-aware)
 * Last modified: 2026-07-22 | Agent
 * Completeness: 100
 */
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { BrandMark } from '@/components/brand/BrandMark';
import { StripePricingTable } from '@/components/billing/StripePricingTable';
import { PricingPlanFeatures } from '@/components/pricing/PricingPlanFeatures';
import { PricingSubheadAb } from '@/components/pricing/PricingSubheadAb';
import { AppFooter } from '@/components/shell/AppFooter';
import { PageMotion } from '@/components/motion/PageMotion';
import { DesignStyleToggle } from '@/components/design-style-toggle';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { APP_ROUTES } from '@/lib/appRoutes';
import { PRODUCT_NAME, PRODUCT_TERMS } from '@/lib/productTerms';
import { AUTH_CONFIG } from '@/lib/auth-config';
import { REMEDIATION } from '@/lib/remediationRoutes';
import {
  assignPricingSubheadVariant,
  PRICING_SUBHEAD_EXPERIMENT_ID,
} from '@/lib/pricingSubheadExperiment';
import { trackExperimentEvent } from '@/lib/siteAnalytics';

export default function PricingPage() {
  const pricingTableRef = useRef<HTMLDivElement>(null);

  const onSupportClick = () => {
    trackExperimentEvent(
      PRICING_SUBHEAD_EXPERIMENT_ID,
      assignPricingSubheadVariant(),
      'support_click'
    );
  };

  return (
    <PageMotion variant="body">
      <div className="min-h-screen flex flex-col bg-background text-foreground ui-page-hierarchy">
        <header className="shrink-0 border-b border-border/40 bg-background/90 backdrop-blur-sm">
          <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <Link to={APP_ROUTES.home} className="flex items-center gap-2 min-w-0">
              <BrandMark size={24} ghostAccent="darker" />
              <span className="ab-brand-wordmark text-sm truncate">{PRODUCT_NAME}</span>
            </Link>
            <div className="flex items-center gap-2 shrink-0">
              <DesignStyleToggle variant="inline" />
              <Button size="sm" variant="ghost" asChild>
                <Link to={APP_ROUTES.help}>Support</Link>
              </Button>
              <Button size="sm" actionTier="secondary" asChild>
                <Link
                  to={`${AUTH_CONFIG.signInPath}?next=${encodeURIComponent(REMEDIATION.dashboard)}`}
                >
                  Sign in
                </Link>
              </Button>
            </div>
          </div>
        </header>

        <main className="flex-1 w-full px-4 sm:px-6 py-8 sm:py-12 space-y-10 sm:space-y-12">
          <div className="max-w-3xl mx-auto text-center space-y-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Simple plans
            </p>
            <h1 className="text-2xl sm:text-4xl font-bold text-foreground tracking-tight">
              {PRODUCT_TERMS.productBrand} pricing
            </h1>
            <PricingSubheadAb pricingTableRef={pricingTableRef} />
            <div className="flex justify-center pt-1">
              <Badge
                variant="secondary"
                className="text-xs sm:text-sm font-medium px-3 py-1.5 bg-[hsl(var(--ab-green-mist))] text-[var(--auth-accent)] border border-[hsl(var(--ab-green-sage))] dark:bg-[hsl(var(--ab-green-deep))]/20 dark:text-[hsl(var(--ab-green-mist))] dark:border-[hsl(var(--ab-green-sage))]/40"
              >
                Save 17% with our Yearly Plan
              </Badge>
            </div>
          </div>

          <section aria-labelledby="pricing-plans-heading" className="space-y-4">
            <h2 id="pricing-plans-heading" className="sr-only">
              Plan features
            </h2>
            <PricingPlanFeatures />
          </section>

          <section ref={pricingTableRef} aria-labelledby="pricing-checkout-heading" className="space-y-3">
            <div className="max-w-5xl mx-auto text-center space-y-1">
              <h2
                id="pricing-checkout-heading"
                className="text-sm font-medium uppercase tracking-wide text-muted-foreground"
              >
                Choose your plan
              </h2>
              <p className="text-xs text-muted-foreground">
                Secure checkout via Stripe. Enter a promotion code on the payment page before you pay.
              </p>
            </div>
            <StripePricingTable />
          </section>

          <p className="max-w-2xl mx-auto text-center text-xs text-muted-foreground leading-relaxed">
            Questions about enterprise or migration from Zapier, Make, or n8n?{' '}
            <Link
              to={APP_ROUTES.help}
              className="underline underline-offset-2 hover:text-foreground"
              onClick={onSupportClick}
            >
              Visit support
            </Link>{' '}
            or email your administrator.
          </p>
        </main>

        <AppFooter />
      </div>
    </PageMotion>
  );
}
